<script lang="ts">
  import { translate } from "../../lib/translate";
  import Svg from "../UI/Svg.svelte";
  import { dialogTypes } from "../../lib/constants";
  import { dialogData } from "../../stores/dialogDataStore";
  import { payload } from "../../stores/payloadStore";
  import { isClient } from "../../lib/roles";
  import { user } from "../../stores/userStore";
  import { onDestroy, onMount } from "svelte";
  import { Capacitor } from "@capacitor/core";
  import { Keyboard } from "@capacitor/keyboard";
  import { storage } from "lib";

  export let avatarUrl: string;
  export let clientName: string;
  export let isFileUploading: boolean;

  let keyboardIsOpen = false;
  let keyboardHeight = 0;
  let recordingStage: number = 1;
  let inputElement: HTMLInputElement;
  let chatInput: HTMLTextAreaElement;
  let actionsElement: HTMLDivElement;
  let files: File[] = [];
  let showChatHelper: boolean;

  const onFileSelected = (e: any): void => {
    files = e.target.files;
    uploadFile(files);
  };

  const incrementStage = (): void => {
    if (recordingStage === 3) {
      recordingStage = 0;
    }
    recordingStage += 1;
  };

  // Auto-resize the textarea to match its content, up to a 64px cap.
  // Purely a UX feature (multi-line growth) — not a Capacitor workaround.
  //
  // Platform-specific handling is required because the two platforms
  // have opposing quirks around resizing a focused textarea:
  //
  //   Android Chromium: resizing a focused textarea synchronously inside
  //   an `input` event (especially on backspace, when content shrinks)
  //   snaps the caret to position 0. We defer to rAF and save/restore
  //   the selection to work around it.
  //
  //   iOS WKWebView: `setSelectionRange` on a focused textarea triggers
  //   an implicit "scroll into view", which with the on-screen keyboard
  //   up causes the sticky chat bar to be pushed behind the keyboard on
  //   every keystroke. iOS doesn't have the Android caret-reset bug, so
  //   the original synchronous resize works correctly there.
  //
  // NB: detect Android via UA, not `Capacitor.getPlatform()`. The bug
  // lives in Android Chromium itself, so it fires in `npm run dev` /
  // Chrome-on-Android (where Capacitor reports `'web'`) just as it does
  // in the native Capacitor WebView.
  const isAndroid =
    typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
  let resizePending = false;

  const resizeNow = (): void => {
    if (!chatInput) return;
    chatInput.style.height = "0"; // reset scrollHeight
    const { scrollHeight } = chatInput;
    chatInput.style.height = `${scrollHeight > 64 ? 64 : scrollHeight}px`;
  };

  const autoResize = (): void => {
    if (!isAndroid) {
      // iOS / web: keep the original synchronous behavior.
      resizeNow();
      return;
    }

    // Android: defer to rAF and restore caret around the height change.
    if (resizePending) return;
    resizePending = true;
    requestAnimationFrame(() => {
      resizePending = false;
      if (!chatInput) return;

      const isActive = document.activeElement === chatInput;
      const start = chatInput.selectionStart;
      const end = chatInput.selectionEnd;

      resizeNow();

      if (isActive && start !== null && end !== null) {
        try {
          chatInput.setSelectionRange(start, end);
        } catch {
          // Some browsers throw if the element isn't selectable; ignore.
        }
      }
    });
  };

  // Read the value directly from the DOM and push it into the store.
  // Crucially, we do NOT bind:value — Svelte's generated bind code writes
  // the value back to the element on every reactive tick, which on
  // Android Chromium resets the caret to 0 during or after IME
  // composition commits (space, "?", Enter, backspace mid-word).
  const onInput = (e: Event): void => {
    const target = e.currentTarget as HTMLTextAreaElement;
    payload.set(target.value);
    autoResize();
  };

  // One-way sync: store -> DOM, but ONLY when the DOM value actually
  // differs from the store. Any redundant `.value` write during or right
  // after an IME composition commit causes Android Chromium (Capacitor
  // WebView and Chrome alike) to snap the caret to position 0. That's
  // why `bind:value` is avoided below in favor of a manual `on:input`
  // handler — Svelte's generated bind code writes `.value` back on every
  // store tick, which is enough to trigger the same caret reset even
  // when we don't write it ourselves.
  $: if (chatInput && chatInput.value !== $payload) {
    chatInput.value = $payload;
    autoResize();
  }

  onDestroy(() => {
    if (Capacitor.getPlatform() === 'android') {
      Keyboard.removeAllListeners();
    }
  });

  let isMobile = true;

  onMount(async (): Promise<void> => {
    isMobile = /Mobi|Android/i.test(navigator.userAgent);
    showChatHelper = (await storage.get<boolean>("showChatHelper")) === true;
    autoResize();

    // Add keyboard listeners for Capacitor Android only
    if (Capacitor.getPlatform() === 'android') {
      Keyboard.addListener('keyboardWillShow', (info) => {
        keyboardIsOpen = true;
        keyboardHeight = info.keyboardHeight;
      });

      Keyboard.addListener('keyboardWillHide', () => {
        keyboardIsOpen = false;
        keyboardHeight = 0;
      });
    }
  });

  const onSubmit = () => {
    if ($payload !== "") {
      sendMessage($payload);
      $payload = "";
      chatInput.focus();
    }
  };

  export let uploadFile: (files: File[] | null) => Promise<void>;
  export let sendMessage: (payload: string) => Promise<void>;
</script>

<div
  class="
    {Capacitor.getPlatform() === 'android' ? 'fixed' : 'sticky'}
    bottom-0
    left-0
    p-4
    {Capacitor.getPlatform() === 'ios' ? 'pb-8' : ''}
    {isFileUploading ? 'mt-10' : 'mt-4'}
    flex
    items-center
    gap-4
    bg-white
    text-slate-900
    border-t
    border-slate-300
    box-border
    dark:bg-zinc-800
    dark:border-zinc-700
    dark:text-slate-100
    {Capacitor.getPlatform() === 'android' ? 'w-full max-w-screen-sm mx-auto' : ''}
    chat-input-bar
  "
  style="bottom: {Capacitor.getPlatform() === 'android' && keyboardHeight > 0 ? (keyboardHeight - 20) + 'px' : '0'}"
  bind:this={actionsElement}
>
  <!-- class:mt-24={isFileUploading} -->

  <!-- <div>
    <span class="text-xxs">Video se otprema</span>
    <IndeterminateProgressBarComponent/>
  </div> -->

  {#if showChatHelper && keyboardIsOpen && ($user.id === 13 || $user.id === 15 || $user.id === 17833 || $user.id === 87649)}
    <div class="tooltip flex justify-center">
      <div class="myblur rounded-xl shadow-md flex flex-row items-center">
        <img
          class="mr-2"
          style="border-radius: 96px; width: 36px; height: 36px;"
          alt="Avatar"
          height="28"
          width="28"
          src={avatarUrl ? avatarUrl : "/default-avatar.png"}
        />
        <p class="ml-2">
          {clientName}
        </p>
      </div>
    </div>
  {/if}

  {#if recordingStage === 1}
    <textarea
      class="
          p-0
          grow
          leading-none
          outline-0
          bg-white
          text-slate-900
          border-0
          focus:ring-0
          dark:bg-zinc-800
          dark:text-slate-100
        "
      rows="1"
      placeholder={translate("TYPE_HERE")}
      bind:this={chatInput}
      on:input={onInput}
      on:focus={() => {
        if (!Capacitor.isNativePlatform()) {
          keyboardIsOpen = true;
        }
      }}
      on:blur={() => {
        if (!Capacitor.isNativePlatform()) {
          keyboardIsOpen = false;
        }
      }}
      on:keypress={(event) => {
        if (event.key === "Enter") {
          if (isMobile) {
          } else {
            if (!event.shiftKey) {
              event.preventDefault();
              onSubmit();
            }
          }
        }
      }}
    />
  {/if}

  <div class="flex gap-4">
    {#if recordingStage === 1 && !isClient($user)}
      <Svg
        name="template-message"
        size={24}
        customColor="bg-black dark:bg-white"
        on:click={() => {
          $dialogData.type = dialogTypes.TEMPLATE_MESSAGES;
          $dialogData.data = { uploadFile, sendMessage };
        }}
      />
    {/if}
    {#if recordingStage === 1}
      <Svg
        name="camera"
        size={24}
        customColor="bg-black dark:bg-white"
        on:click={() => inputElement.click()}
      />
      <input
        multiple
        class="hidden"
        type="file"
        accept=".jpg, .jpeg, .png, .mp4"
        bind:this={inputElement}
        on:change={(e) => onFileSelected(e)}
      />
    {/if}
    {#if recordingStage === 1}
      <Svg
        name="right-arrow-button"
        size={24}
        customColor="bg-primary-500 dark:bg-primary-500"
        on:click={onSubmit}
      />
    {/if}
  </div>
</div>

<style>
  :global(.cap-native) .chat-input-bar {
    padding-bottom: var(--safe-area-inset-bottom, env(safe-area-inset-bottom))
    
  }

  .tooltip {
    position: absolute;
    bottom: 256px;
    right: 8px;
  }

  .myblur {
    width: fit-content;
    padding: 8px;
    backdrop-filter: blur(8px);
  }

  /* iOS-specific textarea border fix */
  textarea {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
  }

  textarea:focus {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
    -webkit-tap-highlight-color: transparent;
  }
</style>
